import { Popover } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { showNotification } from "../../helpers/showNotification";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import { RowContainer } from "../../pages/login/styles";
import { RoutesNames } from "../../routes/enum";
import { resetPasswordAction } from "../../store/actions/authActions";
import { authSlice } from "../../store/reducers/AuthSlice";
import { CButton, CInput } from "../common";
import { ErrorMessage } from "../ErrorMessage";
import { PasswordRules } from "../registration/PasswordRules";

const initValidateState = {
    hasNumber: false,
    hasCapital: false,
    hasSpecialSymbol: false,
    hasCorrectLength: false,
    hasLowerLetter: false
};

export const ThirdStep = () => {
    const [ruleFields, setRuleFields] = useState<{ [key: string]: boolean }>(initValidateState);
    const { reset, isLoading, error } = useAppSelector((state) => state.authReducer);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const validateFromFields = (value: string) => {
        const isEnLetter = /[a-zA-Z]/.test(value);

        if (!value) {
            setRuleFields(initValidateState);
        }

        if (!isEnLetter) return;

        const hasNumber = !!parseInt(value.replace(/\D/g, ""));
        const hasCapital = RegExp(/\p{Lu}/, "u").test(value);
        const hasSpecialSymbol = RegExp(/[!@#№:;()_+$&*%]/).test(value);
        const hasCorrectLength = value.length >= 8;
        const hasLowerLetter = value !== value.toUpperCase();
        setRuleFields({
            hasNumber,
            hasCapital,
            hasSpecialSymbol,
            hasCorrectLength,
            hasLowerLetter
        });
    };

    const getValuesFromFields = (e: ChangeEvent<HTMLInputElement> | CheckboxChangeEvent) => {
        let value = e.target.value;
        const name = e.target.name as string;

        if (name === "password") {
            validateFromFields(value);
        }

        if (name === "savedInSystem") {
            value = e.target.checked;
        }

        dispatch(authSlice.actions.updateResetPasswordForm({ ...reset, [name]: value }));
    };

    const onSubmitData = async () => {
        try {
            const userRole = await dispatch(
                resetPasswordAction(reset.password, reset.email, reset.role)
            );
            navigate(`/${userRole}/${RoutesNames.PROFILE}`);
        } catch (error) {
            showNotification(t("Server error"), "error");
        }
    };

    const stepIndex = Object.values(ruleFields).filter((el) => el);
    const isSamePassword = reset.password === reset.repeated_password;

    return (
        <RowContainer>
            <Popover
                placement="bottomLeft"
                content={<PasswordRules stepIndex={stepIndex.length} ruleFields={ruleFields} />}
                title={t("Your password must includes:")}
                trigger="focus"
            >
                <CInput
                    isPasswordField
                    label={t("Create password")}
                    name="password"
                    placeholder={t("Enter a new password")}
                    onChange={getValuesFromFields}
                />
            </Popover>
            <CInput
                isPasswordField
                label={t("Repeat password")}
                placeholder={t("Repeat password")}
                name="repeated_password"
                onChange={getValuesFromFields}
            />
            <CButton
                onClick={onSubmitData}
                loading={isLoading}
                disabled={!reset.password || !reset.repeated_password || !isSamePassword}
            >
                {t("Save and login")}
            </CButton>
            {error && <ErrorMessage message={error} />}
        </RowContainer>
    );
};
